// Markdown rendering: wikilinks, clickable #tags, task lists, attachment embeds.
import MarkdownIt from 'markdown-it';
import type Token from 'markdown-it/lib/token.mjs';
import { basename, dirname, joinPath, resolveRelative, stripMarkdownExt } from './dom.js';

const IMAGE_EXT = /\.(png|jpe?g|gif|webp|svg|avif|bmp|ico)$/i;
const EXTERNAL = /^(?:[a-z][a-z0-9+.-]*:|\/\/)/i;

export interface WikiIndex {
  /** Every blob path in the vault (files of any type), sorted. */
  paths: string[];
  /** lowercase basename → paths with that basename. */
  byBasename: Map<string, string[]>;
}

export interface RenderContext {
  path: string;
  index: WikiIndex;
  rawUrl(path: string): string;
}

/** Builds the lookup used to resolve `[[wikilinks]]` the way the server does. */
export function buildWikiIndex(paths: string[]): WikiIndex {
  const byBasename = new Map<string, string[]>();
  for (const path of paths) {
    const key = basename(path).toLowerCase();
    const list = byBasename.get(key);
    if (list) list.push(path);
    else byBasename.set(key, [path]);
  }
  return { paths, byBasename };
}

/** `raw` → vault path, mirroring the server's basename-first resolution. */
export function resolveWikiTarget(index: WikiIndex, raw: string): string | null {
  const target = raw.split('|')[0]!.split('#')[0]!.trim();
  if (!target) return null;
  const withExt = /\.(md|markdown)$/i.test(target) ? target : `${target}.md`;
  const candidates = index.byBasename.get(basename(withExt).toLowerCase());
  if (candidates) {
    const exact = candidates.find((candidate) => candidate.toLowerCase() === withExt.toLowerCase());
    return exact ?? candidates[0]!;
  }
  const byName = index.byBasename.get(basename(target).toLowerCase());
  return byName?.[0] ?? null;
}

function renderCheckbox(checked: boolean): string {
  return `<input type="checkbox" class="task-checkbox" disabled${checked ? ' checked' : ''}> `;
}

/** Adds GitHub-style `- [ ]` / `- [x]` task list rendering. */
function taskListPlugin(md: MarkdownIt): void {
  md.core.ruler.after('inline', 'vault-tasklist', (state) => {
    const tokens = state.tokens;
    for (let i = 2; i < tokens.length; i++) {
      const inline = tokens[i]!;
      if (inline.type !== 'inline' || tokens[i - 1]!.type !== 'paragraph_open') continue;
      const itemOpen = tokens[i - 2]!;
      if (itemOpen.type !== 'list_item_open') continue;
      const first = inline.children?.[0];
      if (!first || first.type !== 'text') continue;
      const marker = /^\[([ xX])\][ \t]+/.exec(first.content);
      if (!marker) continue;
      const checked = marker[1]!.toLowerCase() === 'x';
      first.content = first.content.slice(marker[0].length);
      const checkbox = new state.Token('html_inline', '', 0);
      checkbox.content = renderCheckbox(checked);
      inline.children!.unshift(checkbox);
      itemOpen.attrJoin('class', 'task-list-item');
      itemOpen.attrSet('data-task', checked ? 'checked' : 'unchecked');
      const listOpen = tokens.slice(0, i).reverse().find((token) => token.type === 'bullet_list_open' || token.type === 'ordered_list_open');
      listOpen?.attrSet('class', 'task-list');
    }
  });
}

/** `[[note]]`, `[[note|alias]]`, `![[image.png]]` and clickable `#tags`. */
function wikilinkPlugin(md: MarkdownIt): void {
  md.inline.ruler.before('link', 'vault-wikilink', (state, silent) => {
    const start = state.pos;
    const embed = start > 0 && state.src.charCodeAt(start - 1) === 0x21 /* ! */;
    if (state.src.charCodeAt(start + 1) !== 0x5b /* [ */) return false;
    const end = state.src.indexOf(']]', start + 2);
    if (end < 0) return false;
    const inner = state.src.slice(start + 2, end);
    if (!inner || inner.includes('\n') || inner.includes('[') || inner.includes(']')) return false;
    if (silent) {
      state.pos = end + 2;
      return true;
    }
    if (embed && state.pending.endsWith('!')) state.pending = state.pending.slice(0, -1);
    const context = state.env as RenderContext;
    const [rawTarget = '', rawAlias] = inner.split('|');
    const target = rawTarget.split('#')[0]!.trim();
    const alias = (rawAlias ?? rawTarget.split('#')[0] ?? '').trim() || target;
    const resolved = resolveWikiTarget(context.index, inner);
    if (embed && resolved && IMAGE_EXT.test(resolved)) {
      const image = state.push('image', 'img', 0);
      image.attrs = [
        ['src', context.rawUrl(resolved)],
        ['alt', alias],
        ['class', 'vault-embed'],
        ['loading', 'lazy'],
      ];
      state.pos = end + 2;
      return true;
    }
    const open = state.push('link_open', 'a', 1);
    open.attrs = [
      ['href', '#'],
      ['class', resolved ? 'wikilink' : 'wikilink unresolved'],
      ['data-wikilink', rawTarget.trim()],
    ];
    if (resolved) open.attrs.push(['data-vault-path', resolved]);
    else open.attrs.push(['title', `Unresolved link: ${target}`]);
    const text = state.push('text', '', 0);
    text.content = alias;
    state.push('link_close', 'a', -1);
    state.pos = end + 2;
    return true;
  });

  md.inline.ruler.before('link', 'vault-tag', (state, silent) => {
    if (state.src.charCodeAt(state.pos) !== 0x23 /* # */) return false;
    const previous = state.pos > 0 ? state.src.charCodeAt(state.pos - 1) : 0x20;
    const previousIsWord = /[A-Za-z0-9_/#\-]/.test(String.fromCharCode(previous));
    if (previousIsWord && previous !== 0x20) return false;
    const match = /^#([A-Za-z0-9_\-/]*[A-Za-z_\-/][A-Za-z0-9_\-/]*)/.exec(state.src.slice(state.pos));
    if (!match) return false;
    if (silent) {
      state.pos += match[0].length;
      return true;
    }
    const tag = match[1]!;
    const open = state.push('link_open', 'a', 1);
    open.attrs = [
      ['href', '#'],
      ['class', 'vault-tag'],
      ['data-tag', tag],
    ];
    const text = state.push('text', '', 0);
    text.content = `#${tag}`;
    state.push('link_close', 'a', -1);
    state.pos += match[0].length;
    return true;
  });
}

const md = new MarkdownIt({ html: false, linkify: true, breaks: false, typographer: false });
taskListPlugin(md);
wikilinkPlugin(md);

md.renderer.rules.image = (tokens, idx, options, env, self) => {
  const token = tokens[idx]!;
  const context = env as RenderContext;
  const src = token.attrGet('src') ?? '';
  if (src && !EXTERNAL.test(src) && !src.startsWith('/api/')) {
    token.attrSet('src', context.rawUrl(resolveRelative(context.path, src)));
  }
  token.attrSet('loading', 'lazy');
  token.attrSet('class', 'vault-embed');
  token.attrSet('alt', token.children ? self.renderInlineAsText(token.children, options, env) : '');
  return self.renderToken(tokens, idx, options);
};

md.renderer.rules.link_open = (tokens, idx, options, env, self) => {
  const token = tokens[idx]!;
  // Wikilinks and tags build their own attributes; leave them alone.
  if (token.attrGet('data-wikilink') !== null || token.attrGet('data-tag') !== null) {
    return self.renderToken(tokens, idx, options);
  }
  const context = env as RenderContext;
  const href = token.attrGet('href') ?? '';
  if (href && href !== '#' && !EXTERNAL.test(href)) {
    const target = resolveRelative(context.path, href);
    const known = context.index.paths.includes(target);
    token.attrSet('href', '#');
    token.attrSet('data-vault-path', target);
    token.attrSet('class', known ? 'internal-link' : 'internal-link unresolved');
  } else if (href && EXTERNAL.test(href)) {
    token.attrSet('target', '_blank');
    token.attrSet('rel', 'noreferrer');
  }
  return self.renderToken(tokens, idx, options);
};

/** Renders one markdown document to HTML for the preview pane. */
export function renderMarkdown(source: string, context: RenderContext): string {
  if (!source.trim()) return '<p class="empty-note">Empty note</p>';
  return md.render(source, context as unknown as Record<string, unknown>);
}

/** Renders a fragment (used for conflict / revision previews). */
export function renderInline(source: string, context: RenderContext): string {
  return md.render(source, context as unknown as Record<string, unknown>);
}

/** Plain-text first line of a note, used in quick-switcher and list rows. */
export function firstLine(source: string): string {
  const line = source
    .split(/\r?\n/)
    .map((value) => value.trim())
    .find((value) => value.length > 0 && !value.startsWith('---'));
  return line ? line.replace(/^#+\s*/, '').slice(0, 80) : '';
}

/** Title used when a path is displayed without reading the file. */
export function noteTitle(path: string): string {
  return stripMarkdownExt(basename(path));
}

export { joinPath, dirname };
